import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { SEND_MESSAGE_HELP_TEXT } from 'src/app/core/constants/mapping-keys';
import { DELAY_TIME_MS } from 'src/app/core/constants/typeahead';
import { CareTeamMember } from 'src/app/core/models/care-team-member';
import { EntityType } from 'src/app/core/models/entitlements/entity';
import { DisplayValue, SendMessage } from 'src/app/core/models/message-form';
import { Patient, PatientSearchRow, PatientSearchRowMapper } from 'src/app/core/models/patient';
import { PatientReferralRow } from 'src/app/core/models/patient-referral';
import { ReferenceTable } from 'src/app/core/models/reference-table';
import { CustomDatePipe } from 'src/app/core/pipes/custom-date.pipe';
import { AuthService } from 'src/app/core/services/auth.service';
import { CareTeamService } from 'src/app/core/services/care-team.service';
import { EntityNameCacheService } from 'src/app/core/services/entity-name-cache.service';
import { GlobalModalService } from 'src/app/core/services/global-modal.service';
import { MemberInformationService } from 'src/app/core/services/member-information.service';
import { MessageService } from 'src/app/core/services/message.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { UserService } from 'src/app/core/services/user.service';
import { formatCareTeamMemberName } from 'src/app/core/utils/autocomplete-util';
import { isEmpty } from 'src/app/core/utils/object-util';

@Component({
  selector: 'th-create-new-message',
  templateUrl: './create-new-message.component.html',
  styleUrls: ['./create-new-message.component.scss']
})
export class CreateNewMessageComponent implements OnInit, OnDestroy {
  @Input() patient: PatientSearchRow;
  @Input() referral: PatientReferralRow;
  sendMessage: SendMessage = new SendMessage();
  memberIdSubscription: Subscription;
  memberId: string;
  careTeamMembers: CareTeamMember[] = [];
  allCareTeamMembersSelected = false;
  searchingRecipients = false;
  searchingRecipientsFailed = false;
  tags: DisplayValue[] = [];
  submitting = false;
  errors = {
    selectedUserName: 'Please select a valid recipient',
    message: 'Please provide a message'
  };
  EntityType: typeof EntityType = EntityType;
  // @ViewChild('tagsInput', { static: true }) tagInput: TagInputComponent;
  tagArray: DisplayValue[] = []; // TODO: make less confusing
  subjects: string[];
  loading = false;
  sendMessageHelpText: string;
  recipients: DisplayValue[] = [];
  recipientList: Observable<DisplayValue[]>;
  recipientInput = new Subject<string>();
  messageMaxLength = 10000;
  @ViewChild('replyForm') replyForm: FormGroup;

  constructor(
    public modalService: NgbModal,
    public careTeamService: CareTeamService,
    private authService: AuthService,
    private userService: UserService,
    private messageService: MessageService,
    private toastr: ToastrService,
    private datePipe: CustomDatePipe,
    private globalModalService: GlobalModalService,
    private memberInformationService: MemberInformationService,
    private patientService: PatientService,
    private organizationService: OrganizationService,
    private entityNameCacheService: EntityNameCacheService
  ) {}

  ngOnInit() {
    this.sendMessage.subject = null;

    this.getMappingKeyUserMessage();
    if (this.patient?.memberId) {
      // should this decide??
      this.memberId = this.patient.memberId;
      this.processSubscriptions();
    } else {
      this.memberIdSubscription = this.memberInformationService.memberId.subscribe((memberId) => {
        this.memberId = memberId;
        this.processSubscriptions();
      }); // is this okay should never happen
    }

    this.recipientList = this.recipientInput.pipe(
      debounceTime(DELAY_TIME_MS),
      filter((term) => !isEmpty(term)),
      distinctUntilChanged(),
      tap(() => (this.searchingRecipients = true)),
      switchMap((term) =>
        this.messageService.searchRecipients(term).pipe(
          map((userList) =>
            userList.map((user) => {
              const displayValue: DisplayValue = { display: user.label, value: String(user.id) };
              return displayValue;
            })
          ),
          catchError(() => {
            return of([]);
          })
        )
      ),
      tap(() => (this.searchingRecipients = false))
    );
  }

  getMappingKeyUserMessage() {
    this.sendMessageHelpText = this.organizationService.getCachedMappingKey(SEND_MESSAGE_HELP_TEXT);
  }

  ngOnDestroy() {
    if (this.memberIdSubscription) {
      this.memberIdSubscription.unsubscribe();
    }
  }

  get showOtherSubject(): boolean {
    return this.sendMessage.subject === 'Other';
  }

  getMessageSubjectsObservable(): Observable<ReferenceTable[]> {
    return this.memberId ? this.userService.getMessageSubjects() : of([]);
  }

  getPatientObservable(): Observable<Patient[]> {
    if (this.memberId && this.patient) {
      return of([]); // we already have patient
    } else if (this.memberId) {
      return this.patientService.getPatient(this.memberId).pipe(map((patient) => [patient]));
    } else {
      return of([]);
    }
  }

  getCareTeamObservable(): Observable<CareTeamMember[]> {
    return this.memberId ? this.careTeamService.getCareTeam(this.memberId, true, true) : of([]);
  }

  processSubscriptions() {
    this.loading = true;

    forkJoin([
      this.getCareTeamObservable(),
      this.userService.getMessageTags(),
      this.getMessageSubjectsObservable(),
      this.getPatientObservable()
    ])
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(
        ([careTeam, tags, subjects, patient]: [CareTeamMember[], ReferenceTable[], ReferenceTable[], Patient[]]) => {
          this.processCareTeam(careTeam);
          this.processMessageTags(tags);
          this.processMessageSubjects(subjects);
          this.processPatients(patient);
        }
      );
  }

  private processCareTeam(careTeamMembers: CareTeamMember[]): void {
    this.careTeamMembers = careTeamMembers || [];

    this.careTeamMembers.forEach(
      (careTeamMember) => (careTeamMember.formattedName = formatCareTeamMemberName(careTeamMember))
    );

    if (this.referral) {
      this.entityNameCacheService.getCachedName(this.referral.addedBy, EntityType.USER).subscribe((data) => {
        const item = { display: data, value: this.referral.addedBy } as DisplayValue;
        this.onRecipientAddRemove(item, true, true);
      });
    }
  }

  processMessageTags(result: ReferenceTable[]): void {
    const temp = [];

    result.forEach((r) => {
      const item = { display: r.description, value: r.description } as DisplayValue;
      temp.push(item);
    });

    this.tags = temp;
  }

  processMessageSubjects(result: ReferenceTable[]): void {
    this.subjects = [];

    result.forEach((r) => {
      this.subjects.push(r.description);
    });
  }

  private processPatients(patient: Patient[]) {
    const p = new PatientSearchRowMapper(this.datePipe);

    if (patient.length === 1) {
      this.patient = p.mapRow(patient[0]);
    }

    if (patient.length > 1) {
      console.warn('Received more than one patient');
    }
  }

  selectAllCareTeamMembers() {
    this.careTeamMembers.forEach((careTeamMember) => {
      careTeamMember.selected = this.allCareTeamMembersSelected;
      this.updateRecipients(careTeamMember);
    });
  }

  selectCareTeamMember(careTeamMember: CareTeamMember) {
    this.updateRecipients(careTeamMember);

    this.allCareTeamMembersSelected = this.careTeamMembers.every((teamMember) => teamMember.selected);
  }

  // Used to keep the care team checkboxes in sync with the tag input when adding/removing tags
  onRecipientAddRemove(recipientInfo: DisplayValue, careTeamMemberSelected: boolean, referral = false) {
    const recipientUserId = recipientInfo.value || recipientInfo;

    // this is to check whether selected user is not the logged in one.
    const matchLoggedUser = Number(recipientUserId) === this.authService.user.userId && careTeamMemberSelected === true;
    if (matchLoggedUser && !referral) {
      this.toastr.warning('This message will be saved in your sent items. No need to send it to yourself.');
    }

    const careTeamMember = this.careTeamMembers.find(
      (teamMember) => String(teamMember.userid) === String(recipientUserId)
    );

    if (this.referral && careTeamMember === undefined && !matchLoggedUser && referral) {
      const value = this.messageService.searchRecipients(recipientInfo.display).pipe(
        map((userList) =>
          userList.map((user) => {
            const displayValue: DisplayValue = { display: user.label, value: String(user.id) };
            return displayValue;
          })
        ),
        catchError(() => {
          return of([]);
        })
      );

      value.subscribe((data) => {
        this.recipients = data;
      });
    }

    if (careTeamMember && !matchLoggedUser) {
      careTeamMember.selected = careTeamMemberSelected;

      this.updateRecipients(careTeamMember);
    }

    this.allCareTeamMembersSelected = this.careTeamMembers.every((teamMember) => teamMember.selected);
  }

  clearRecipients() {
    this.allCareTeamMembersSelected = false;
    this.selectAllCareTeamMembers();
  }

  onSubjectChange() {
    this.sendMessage.otherSubject = null;
  }

  // Used to keep the care team checkboxes in sync with the tag input when checking/unchecking checkboxes
  updateRecipients(careTeamMember: CareTeamMember) {
    const recipientIndex = this.recipients.findIndex(
      (recipient) => String(recipient.value) === String(careTeamMember.userid)
    );

    if (recipientIndex < 0 && careTeamMember.selected) {
      const newRecipient: DisplayValue = {
        display: careTeamMember.formattedName,
        value: String(careTeamMember.userid)
      };

      this.recipients = [...this.recipients, newRecipient];
    } else if (recipientIndex >= 0 && !careTeamMember.selected) {
      this.recipients = this.recipients.filter((_, index) => index !== recipientIndex);
    }
  }

  send() {
    this.recipients = this.recipients.filter(
      (item: DisplayValue) =>
        String(item.value) !== String(this.authService.user.userId) && String(item.display) !== 'System Generated'
    );

    if (this.recipients == null || this.recipients.length === 0) {
      this.globalModalService.alert({
        bodyText: 'Please enter sender information'
      });
    } else {
      this.sendMessage.recipientUserIds = this.recipients.map((recipient) => {
        return recipient.value ? String(recipient.value) : String(recipient);
      });

      this.sendMessage.patientId = this.memberId;

      this.sendMessage.tags = this.tagArray.join(', ');

      this.submitting = true;

      if (this.referral) {
        this.sendMessage.referralId = this.referral.referralId;
      }

      this.messageService
        .sendMessage(this.sendMessage)
        .pipe(finalize(() => (this.submitting = false)))
        .subscribe((data) => {
          if (data) {
            this.modalService.dismissAll();
            this.toastr.success('Message sent successfully');
          }
        });
    }
  }

  addTag(tag: string): DisplayValue {
    return { display: tag, value: tag };
  }

  compareSelectedItem(item: DisplayValue, selected: DisplayValue) {
    return item.value === selected.value;
  }
}
